/**
 * The live plan beside the form: the selected budget option's totals, where
 * the rates came from, the checks, the Sheet option and Create. The numbers
 * are the server's last quote — dimmed while a newer one is on its way, and
 * hidden when the draft can't be priced. A polite live region reads out each
 * fresh price (not every keystroke).
 */

import React, { useEffect, useState } from 'react';
import { Button, Card, Switch } from '../../../ui';
import InlineConfirm from '../InlineConfirm';
import type { LiveQuoteStatus } from '../useLiveQuote';
import type { MediaPlanQuote, NeedsAdvertiserStub, RateBasis } from '../types';
import type { PlanCheck } from '../planChecks';
import { connectGoogleWorkspace } from '../googleWorkspace';
import { bannerStyle, fmtDate, fmtInt, fmtUsd, mutedText } from '../formStyles';
import ChecksList from './ChecksList';
import { BudgetOptionTabs } from './PlanLinesSection';

interface Props {
  status: LiveQuoteStatus;
  quote: MediaPlanQuote | null;
  tierIndex: number;
  onTierChange: (index: number) => void;
  checks: PlanCheck[];
  onGoTo: (field: string) => void;
  error: string | null;
  onRetry: () => void;
  needsAdvertiser: NeedsAdvertiserStub | null;
  onAddAdvertiser: () => void;
  addingAdvertiser: boolean;
  ambiguous: { error: string; candidates: Array<{ key: string; name: string }> } | null;
  onPickCandidate: (key: string, name: string) => void;
  /** Create came back 409: the numbers shown are the new ones. */
  pricesChanged: boolean;
  createSheet: boolean;
  onCreateSheetChange: (next: boolean) => void;
  google: { connected: boolean | null; recheck: () => void };
  canCreate: boolean;
  creating: boolean;
  onCreate: () => void;
  createError: React.ReactNode;
  createLabel: string;
  savedAt: number | null;
  saveFailed: boolean;
  onDiscard: () => void;
}

const STATUS_WORDS: Record<LiveQuoteStatus, { text: string; color: string }> = {
  incomplete: { text: 'Not priced yet', color: 'var(--fg-4)' },
  updating: { text: 'Updating…', color: 'var(--info)' },
  current: { text: 'Up to date', color: 'var(--success)' },
  'needs-advertiser': { text: 'Needs an advertiser', color: 'var(--warning)' },
  ambiguous: { text: 'Needs an advertiser', color: 'var(--warning)' },
  error: { text: 'Couldn’t price', color: 'var(--danger)' },
  paused: { text: 'Paused', color: 'var(--warning)' },
};

const BASIS_ORDER: Array<[RateBasis, string]> = [
  ['history', 'from history'],
  ['negotiated', 'negotiated'],
  ['list', 'list'],
  ['seller', 'yours'],
];

/** "2 from history · 1 list · 1 yours" */
export function rateSourcesSummary(quote: MediaPlanQuote): string {
  const counts = new Map<RateBasis, number>();
  for (const r of quote.rates ?? []) if (r.rateType === 'cpm' && r.effective) counts.set(r.effective.basis, (counts.get(r.effective.basis) ?? 0) + 1);
  return BASIS_ORDER.filter(([b]) => counts.get(b))
    .map(([b, words]) => `${counts.get(b)} ${words}`)
    .join(' · ');
}

const fmtTime = (ms: number): string => new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const LiveSummary: React.FC<Props> = (p) => {
  const { status, quote } = p;
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const showNumbers = !!quote && (status === 'current' || status === 'updating' || status === 'error' || status === 'paused');
  const tiers = quote?.preview.tiers ?? [];
  const tier = tiers[Math.min(p.tierIndex, tiers.length - 1)];
  const t = tier?.totals;
  const fix = p.checks.filter((c) => c.tone === 'fix').length;
  const review = p.checks.length - fix;
  const statusWords = STATUS_WORDS[status];

  useEffect(() => {
    if (status !== 'current' || !t) return;
    setAnnouncement(`Prices updated: ${fmtUsd(t.paidCostUsd)} paid media, ${fmtInt(t.paidImpressions)} paid impressions, blended CPM ${fmtUsd(t.blendedCpm)}.`);
  }, [status, t]);

  let blocked: string | null = null;
  if (!p.canCreate && !p.creating) {
    if (fix > 0) blocked = `Fix ${fix} ${fix === 1 ? 'check' : 'checks'} to create the plan.`;
    else if (status === 'updating') blocked = 'Waiting for the latest prices…';
    else if (status === 'error') blocked = 'Price the plan first — Retry above.';
    else if (status === 'paused') blocked = 'Prices resume in a minute.';
    else if (status === 'incomplete') blocked = 'Fill in the form to price the plan.';
  }

  return (
    <Card style={styles.card}>
      <div style={styles.head}>
        <div style={styles.eyebrow}>Live plan</div>
        <span style={{ ...styles.status, color: statusWords.color }}>
          <span aria-hidden="true" style={{ ...styles.statusDot, background: statusWords.color }} />
          {statusWords.text}
        </span>
      </div>

      {showNumbers && quote ? (
        <div aria-busy={status === 'updating' || undefined} style={{ display: 'flex', flexDirection: 'column', gap: 12, opacity: status === 'current' ? 1 : 0.6 }}>
          <div>
            <div style={styles.advertiser}>{quote.advertiser.name}</div>
            <div style={mutedText}>
              {quote.agencyLabel ? `${quote.agencyLabel} · ` : ''}
              {fmtDate(quote.request.flightStart)} – {fmtDate(quote.request.flightEnd)}
            </div>
          </div>
          <BudgetOptionTabs quote={quote} tierIndex={p.tierIndex} onTierChange={p.onTierChange} />
          {t && (
            <dl style={styles.stats}>
              <div style={styles.statWide}>
                <dt style={styles.statLabel}>Paid media</dt>
                <dd style={styles.statBig}>{fmtUsd(t.paidCostUsd)}</dd>
              </div>
              <div>
                <dt style={styles.statLabel}>Paid impressions</dt>
                <dd style={styles.statValue}>{fmtInt(t.paidImpressions)}</dd>
              </div>
              <div>
                <dt style={styles.statLabel}>Blended CPM</dt>
                <dd style={styles.statValue}>{fmtUsd(t.blendedCpm)}</dd>
              </div>
              <div>
                <dt style={styles.statLabel}>Added value</dt>
                <dd style={styles.statValue}>{t.addedValueImpressions > 0 ? fmtInt(t.addedValueImpressions) : 'None'}</dd>
              </div>
              <div>
                <dt style={styles.statLabel}>Total delivered</dt>
                <dd style={styles.statValue}>{fmtInt(t.totalImpressions)}</dd>
              </div>
            </dl>
          )}
          {rateSourcesSummary(quote) && <div style={styles.line}>Rates: {rateSourcesSummary(quote)}</div>}
        </div>
      ) : status === 'needs-advertiser' && p.needsAdvertiser ? (
        <div style={styles.prompt}>
          <span>{p.needsAdvertiser.name} isn't in the advertiser list yet, so it can't be priced.</span>
          <Button size="sm" variant="primary" onClick={p.onAddAdvertiser} disabled={p.addingAdvertiser}>
            {p.addingAdvertiser ? `Looking up ${p.needsAdvertiser.name}…` : `Add ${p.needsAdvertiser.name}`}
          </Button>
        </div>
      ) : status === 'ambiguous' && p.ambiguous ? (
        <div style={styles.prompt}>
          <span>Which one do you mean?</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {p.ambiguous.candidates.map((c) => (
              <Button key={c.key} size="sm" variant="subtle" onClick={() => p.onPickCandidate(c.key, c.name)}>
                {c.name}
              </Button>
            ))}
          </div>
        </div>
      ) : status === 'updating' ? (
        <div style={mutedText}>Pricing the plan…</div>
      ) : status === 'incomplete' ? (
        <div style={mutedText}>Add the advertiser, budget and flight to see the plan priced here.</div>
      ) : null}

      {status === 'error' && (
        <div role="alert" style={bannerStyle('danger')}>
          <span style={{ flex: 1 }}>Couldn’t price the plan: {p.error}</span>
          <Button size="sm" variant="ghost" onClick={p.onRetry}>
            Retry
          </Button>
        </div>
      )}
      {status === 'paused' && <div style={bannerStyle('warning')}>Too many requests right now. Prices update again in a minute.</div>}
      {p.pricesChanged && status === 'current' && (
        <div role="alert" style={bannerStyle('warning')}>
          Prices changed since this quote. Review the new numbers, then create the plan.
        </div>
      )}

      {p.checks.length > 0 && (
        <div style={styles.checks}>
          <div style={styles.line}>
            Checks: {fix > 0 ? `${fix} to fix` : 'nothing to fix'}
            {review > 0 ? ` · ${review} to review` : ''}
          </div>
          <ChecksList checks={p.checks} onGoTo={p.onGoTo} />
        </div>
      )}

      <div style={styles.footer}>
        <Switch
          size="sm"
          checked={p.createSheet && p.google.connected === true}
          onChange={p.onCreateSheetChange}
          disabled={p.google.connected !== true || p.creating}
          label="Google Sheet for the client"
          description={
            p.google.connected === null
              ? 'Checking your Google connection…'
              : p.google.connected
                ? 'Made in your Drive when the plan is created.'
                : 'Connect Google Workspace to make one.'
          }
        />
        {p.google.connected === false && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Button size="sm" variant="subtle" onClick={connectGoogleWorkspace}>
              Connect Google Workspace
            </Button>
            <Button size="sm" variant="ghost" onClick={p.google.recheck}>
              Check again
            </Button>
          </div>
        )}
        <Button variant="primary" fullWidth onClick={p.onCreate} disabled={!p.canCreate || p.creating}>
          {p.creating ? 'Creating…' : p.createLabel}
        </Button>
        {blocked && <div style={{ ...mutedText, textAlign: 'center' }}>{blocked}</div>}
        {p.createError && (
          <div role="alert" style={bannerStyle('danger')}>
            {p.createError}
          </div>
        )}
        <div style={styles.draftLine}>
          {p.saveFailed ? (
            <span style={{ color: 'var(--warning)' }}>This browser isn't saving drafts.</span>
          ) : p.savedAt ? (
            <span>Draft saved {fmtTime(p.savedAt)}</span>
          ) : (
            <span>Drafts save as you type.</span>
          )}
          {!confirmDiscard && (
            <Button size="sm" variant="ghost" onClick={() => setConfirmDiscard(true)} disabled={p.creating}>
              Discard
            </Button>
          )}
        </div>
        {confirmDiscard && (
          <InlineConfirm title="Discard this draft?" confirmLabel="Discard draft" cancelLabel="Keep editing" tone="danger" onConfirm={p.onDiscard} onCancel={() => setConfirmDiscard(false)}>
            Everything entered here is removed. Saved plans aren't affected.
          </InlineConfirm>
        )}
      </div>

      <div aria-live="polite" style={styles.srOnly}>
        {announcement}
      </div>
    </Card>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: { display: 'flex', flexDirection: 'column', gap: 14 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  eyebrow: { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-3)' },
  status: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 },
  statusDot: { width: 7, height: 7, borderRadius: '50%' },
  advertiser: { fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--fg-1)' },
  stats: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 12px', margin: 0 },
  statWide: { gridColumn: '1 / -1' },
  statLabel: { fontSize: 11, fontWeight: 600, color: 'var(--fg-4)' },
  statBig: { margin: 0, fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 700, color: 'var(--fg-1)', fontVariantNumeric: 'tabular-nums' },
  statValue: { margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--fg-1)', fontVariantNumeric: 'tabular-nums' },
  line: { fontSize: 12.5, fontWeight: 600, color: 'var(--fg-2)' },
  prompt: { display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start', fontSize: 13, color: 'var(--fg-2)' },
  checks: { display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' },
  footer: { display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' },
  draftLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--fg-4)' },
  srOnly: { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 },
};

export default LiveSummary;
